import type { BridgeContext, BridgeFeatures, BridgeInvokeHandler } from "./types";
import { bridgeMethods } from "./methods";

type AnyBridgeMethod = (context: BridgeContext, payload: unknown) => Promise<unknown>;

/**
 * Builds the `BridgeFeatures` map expected by `createWorkerBridge` from the
 * registered `bridgeMethods`.
 *
 * Each method is wrapped so that it receives the `data` field from the bridge
 * message payload, and returns `true` as a default acknowledgement when the
 * method returns `undefined`.
 *
 * Product-provided callbacks (e.g. `openModal`, `showFlag`) are accessed via
 * `getBridgeContext().bridgeConfig` at invocation time rather than being passed
 * in directly, so the bridge features map never needs to be rebuilt when the
 * product's bridge prop changes.
 */
export const createBridgeFeatures = (getBridgeContext: () => BridgeContext): BridgeFeatures => {
	return Object.entries(bridgeMethods).reduce((features, [methodName, method]) => {
		features[methodName] = convertToBridgeInvokeHandler((payload: unknown) => {
			// Call getBridgeContext() at invocation time, not creation time, so each
			// bridge call always uses the latest context rather than a stale snapshot.
			return (method as AnyBridgeMethod)(getBridgeContext(), payload);
		});
		return features;
	}, {} as BridgeFeatures);
};

const convertToBridgeInvokeHandler = (method?: Function): BridgeInvokeHandler => {
	return async ({ data }) => {
		if (method) {
			return (await method(data)) ?? true;
		}
		return false;
	};
};
